import { useSearchParams } from "react-router-dom";

import { IAdmin } from "@/interfaces/admin.interface";
import { ICustomer } from "@/interfaces/customer.interface";
import { IEmployee } from "@/interfaces/employee.interface";
import { customerUser } from "@/interfaces/interfaces";
import { IManager } from "@/interfaces/manager.interface";
import { IProspect, IProspectUser } from "@/interfaces/prospect.interface";
import { isAdminDomain, renderFullName } from "@/lib/utils";
import {
  useGetAllCustomerByWSQuery,
  useGetProspectUserQuery,
  useGetProspectsQuery,
} from "@/redux/api/clientQuery";
import {
  useGetCustomerProjectQuery,
  useGetProjectsByStatusQuery,
} from "@/redux/api/projectsQuery";
import {
  useGetUserListQuery,
  useGetUsersByWSQuery,
} from "@/redux/api/userQuery";
import {
  useGetLoadTypeQuery,
  useGetWasteTypeQuery,
} from "@/redux/api/wasteManagementTypeQuery";
import { useGetWorkspacesQuery } from "@/redux/api/workspaceQuery";

type keyType =
  | "employee"
  | "admin"
  | "customer"
  | "subcontractor"
  | "project"
  | "prospect"
  | "customerUser"
  | "prospectUser"
  | "manager"
  | "loadType"
  | "wasteType"
  | "allProject";

export type filterItem = { label: string; id: string };
export type labelValueType = { label: string; value: string };

// Simplified return type using a map
export type filterMapType = { [key: string]: filterItem[] };

export const useGetFilter = (keys: keyType[]): filterMapType => {
  // employee
  const { data: A_employee } = useGetUserListQuery(
    {
      userType: "employee",
    },
    {
      skip: !isAdminDomain || !keys.includes("employee"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const A_employeeList =
    (A_employee?.data as IEmployee[])?.map((item) => ({
      label: item?.user?.firstName + " " + item?.user?.lastName || "",
      id: item.id || "",
    })) || [];

  // employee by ws
  const { data: employee } = useGetUsersByWSQuery(
    {
      userType: "employee",
    },
    {
      skip: isAdminDomain || !keys.includes("employee"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const employeeList =
    (employee?.data as IEmployee[])?.map((item) => ({
      label: item?.user?.firstName + " " + item?.user?.lastName || "",
      id: item.id || "",
    })) || [];

  // workspace
  const { data: Workspaces } = useGetWorkspacesQuery("", {
    skip: !isAdminDomain,
  });
  const workspaceList =
    Workspaces?.data.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  // customer
  const { data: customers } = useGetAllCustomerByWSQuery(
    { user: "", status: "ACTIVE" },
    {
      skip: isAdminDomain || !keys.includes("customer"),
      refetchOnMountOrArgChange: true,
    },
  );

  const customerList =
    (customers as GeneralResponse<ICustomer[]>)?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  /*{ This is customer and its respective projects }*/
  const [params] = useSearchParams();

  const customerId = params.get("customerId") || params.get("customer") || "";

  const { data: projects } = useGetCustomerProjectQuery(
    { customerId },
    {
      skip: !customerId || !keys.includes("project"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const { data: projectS } = useGetProjectsByStatusQuery(
    {
      status: "ONGOING",
    },
    {
      skip: !keys.includes("project"),
    },
  );
  const { data: allProject } = useGetProjectsByStatusQuery(
    {
      status: "",
    },
    {
      skip: !keys.includes("allProject"),
    },
  );

  const allProjectsList =
    allProject?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  const projectList =
    projects?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) ||
    projectS?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) ||
    [];

  // Prospect
  const { data: prospects } = useGetProspectsQuery("", {
    skip: !keys.includes("prospect"),
    // refetchOnMountOrArgChange: true,
  });

  const prospectList =
    (prospects as GeneralResponse<IProspect[]>)?.data?.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  // prospect user
  const { data: prospectUsers } = useGetProspectUserQuery(
    {},

    {
      skip: isAdminDomain || !keys.includes("prospectUser"),
      // refetchOnMountOrArgChange: true,
    },
  );
  const prospectUsersList =
    (prospectUsers as GeneralResponse<IProspectUser[]>)?.data?.map((item) => ({
      label: item?.firstName || "" + item?.lastName || "",
      id: item.id || "",
    })) || [];

  // customer user
  const { data: customerUsers } = useGetAllCustomerByWSQuery(
    { user: "user", status: "ACTIVE" },
    {
      skip: isAdminDomain || !keys.includes("customerUser"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const customerUserList =
    (customerUsers?.data as customerUser[])?.map((item) => ({
      label: item?.user?.firstName || "" + item?.user?.lastName || "",
      id: item.id || "",
    })) || [];

  const { data: admins } = useGetUsersByWSQuery(
    {
      userType: "admin",
      status: "ACTIVE",
    },
    {
      skip: isAdminDomain || !keys.includes("admin"),
      // refetchOnMountOrArgChange: true,
    },
  );
  const adminsList = admins
    ? (admins?.data as IAdmin[])?.map((item) => ({
        label: renderFullName({
          params: item,
        }),
        id: item.id || "",
      }))
    : [];

  const { data: managers } = useGetUsersByWSQuery(
    {
      userType: "manager",
      status: "ACTIVE",
    },
    {
      skip: isAdminDomain || !keys.includes("manager"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const managersList = managers
    ? (managers?.data as IManager[])?.map((item) => ({
        label: renderFullName({
          params: item,
        }),
        id: item.id || "",
      }))
    : [];

  const { data: wasteType } = useGetWasteTypeQuery(
    {},
    {
      skip: isAdminDomain || !keys.includes("loadType"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const wasteTypeList =
    wasteType?.data.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  const { data: loadType } = useGetLoadTypeQuery(
    {},
    {
      skip: isAdminDomain || !keys.includes("wasteType"),
      // refetchOnMountOrArgChange: true,
    },
  );

  const loadTypeList =
    loadType?.data.map((item) => ({
      label: item?.name || "",
      id: item.id || "",
    })) || [];

  return {
    A_employeeList,
    employeeList,
    workspaceList,
    customerList,
    projectList,
    prospectList,
    customerUserList,
    prospectUsersList,
    adminsList,
    managersList,
    wasteTypeList,
    loadTypeList,
    allProjectsList,
  };
};

export default useGetFilter;
